/**
 * SSOErrorHandler
 * Servicio para manejar errores de SSO en la URL
 * Principio: Single Responsibility - Solo maneja errores SSO
 */

/**
 * Parsea los parámetros de error SSO desde la query string de la URL
 * El backend redirige con: ?sso_error=...&error_code=...&sso_redirect=1
 * @returns {Object|null} Parámetros de error parseados o null si no hay errores
 */
export function parseSSOErrorParams() {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    
    const ssoError = urlParams.get('sso_error');
    const errorCode = urlParams.get('error_code');
    const ssoRedirect = urlParams.get('sso_redirect');
    const timestamp = urlParams.get('timestamp');

    // Solo considerar como error SSO si tiene los parámetros mínimos
    if (!ssoError || !errorCode) {
      return null;
    }

    return {
      error: decodeURIComponent(ssoError),
      errorCode: errorCode,
      isSSORedirect: ssoRedirect === '1',
      timestamp: timestamp,
      isConfigError: errorCode === 'config_not_found',
    };
  } catch (error) {
    console.error('[SSO Error Handler] Error parsing URL params:', error);
    return null;
  }
}

/**
 * Verifica si la URL actual contiene errores SSO
 * @returns {boolean}
 */
export function hasSSOErrorInUrl() {
  const errorParams = parseSSOErrorParams();
  return errorParams !== null;
}

/**
 * Limpia los parámetros de error SSO de la URL
 * Esto evita que se mantengan en la URL después de mostrar el error
 */
export function clearSSOErrorFromUrl() {
  try {
    const url = new URL(window.location.href);
    const params = url.searchParams;
    
    // Remover parámetros de error SSO
    params.delete('sso_error');
    params.delete('error_code'); 
    params.delete('sso_redirect');
    params.delete('timestamp');
    
    // Actualizar URL sin recargar la página
    const newUrl = url.pathname + (params.toString() ? '?' + params.toString() : '');
    window.history.replaceState({}, document.title, newUrl);
    
    console.log('[SSO Error Handler] Error params cleared from URL');
  } catch (error) {
    console.error('[SSO Error Handler] Error clearing URL params:', error);
  }
}

/**
 * Procesa y maneja errores SSO encontrados en la URL
 * @returns {{hasError: boolean, errorData?: Object, shouldBlockSSO?: boolean}}
 */
export function processSSOErrors() {
  const errorParams = parseSSOErrorParams();
  
  if (!errorParams) {
    return { hasError: false };
  }

  console.log('[SSO Error Handler] 🚫 SSO error detected:', errorParams);

  // Limpiar URL inmediatamente
  clearSSOErrorFromUrl();

  // Determinar si se debe bloquear SSO automático
  const shouldBlockSSO = errorParams.isConfigError || errorParams.isSSORedirect;

  // Log específico para errores de configuración
  if (errorParams.isConfigError) {
    console.warn('[SSO Error Handler] ⚠️ Config error detected - SSO auto-login will be disabled');
  }

  return {
    hasError: true,
    errorData: {
      message: errorParams.error,
      code: errorParams.errorCode,
      timestamp: errorParams.timestamp,
      isConfigError: errorParams.isConfigError,
    },
    shouldBlockSSO,
  };
}

/**
 * Marca que SSO automático debe estar deshabilitado por errores
 * Guarda un flag en sessionStorage para evitar reintentos automáticos
 */
export function disableAutoSSOForSession() {
  try {
    sessionStorage.setItem('sso_auto_disabled', '1');
    sessionStorage.setItem('sso_auto_disabled_timestamp', Date.now().toString());
    console.log('[SSO Error Handler] 🚫 Auto SSO disabled for session');
  } catch (error) {
    console.warn('[SSO Error Handler] Could not save to sessionStorage:', error);
  }
}

/**
 * Verifica si SSO automático está deshabilitado para la sesión actual
 * @returns {boolean}
 */
export function isAutoSSODisabledForSession() {
  try {
    const disabled = sessionStorage.getItem('sso_auto_disabled');
    const timestamp = sessionStorage.getItem('sso_auto_disabled_timestamp');
    
    if (disabled !== '1') {
      return false;
    }

    // Auto-habilitar después de 5 minutos
    if (timestamp) {
      const disabledTime = parseInt(timestamp);
      const fiveMinutes = 5 * 60 * 1000;
      
      if (Date.now() - disabledTime > fiveMinutes) {
        // Limpiar flag expirado
        sessionStorage.removeItem('sso_auto_disabled');
        sessionStorage.removeItem('sso_auto_disabled_timestamp');
        console.log('[SSO Error Handler] 🔄 Auto SSO re-enabled after timeout');
        return false;
      }
    }

    return true;
  } catch (error) {
    console.warn('[SSO Error Handler] Could not check sessionStorage:', error);
    return false;
  }
}

/**
 * Habilita SSO automático nuevamente (por ejemplo, para login manual)
 */
export function enableAutoSSOForSession() {
  try {
    sessionStorage.removeItem('sso_auto_disabled');
    sessionStorage.removeItem('sso_auto_disabled_timestamp');
    console.log('[SSO Error Handler] ✅ Auto SSO re-enabled manually');
  } catch (error) {
    console.warn('[SSO Error Handler] Could not clear sessionStorage:', error);
  }
}